// LegalPage.tsx — Terms of Service and Privacy Policy
// Routes: /terms and /privacy
// These URLs are required by Apple App Store during review

import React from 'react';

const S = {
  root:    { minHeight:'100vh', background:'#000', color:'#fff', fontFamily:'monospace' } as React.CSSProperties,
  header:  { borderBottom:'1px solid #1a1a1a', padding:'16px 32px', display:'flex', alignItems:'center', justifyContent:'space-between' } as React.CSSProperties,
  logo:    { color:'#32CD32', fontSize:'14px', fontWeight:700, letterSpacing:'4px' } as React.CSSProperties,
  body:    { maxWidth:'800px', margin:'0 auto', padding:'48px 24px 80px' } as React.CSSProperties,
  h1:      { color:'#32CD32', fontSize:'20px', fontWeight:700, letterSpacing:'2px', marginBottom:'4px' } as React.CSSProperties,
  h2:      { color:'#32CD32', fontSize:'13px', fontWeight:700, letterSpacing:'1px', marginTop:'36px', marginBottom:'8px', borderBottom:'1px solid #1a1a1a', paddingBottom:'8px' } as React.CSSProperties,
  meta:    { color:'#555', fontSize:'11px', marginBottom:'32px' } as React.CSSProperties,
  p:       { color:'#aaa', fontSize:'13px', lineHeight:'1.8', marginBottom:'16px' } as React.CSSProperties,
  li:      { color:'#aaa', fontSize:'13px', lineHeight:'1.8', marginBottom:'8px', marginLeft:'20px' } as React.CSSProperties,
  strong:  { color:'#fff', fontWeight:700 } as React.CSSProperties,
  nav:     { display:'flex', gap:'24px' } as React.CSSProperties,
  navLink: (active: boolean) => ({ color: active ? '#32CD32' : '#555', fontSize:'12px', textDecoration:'none', fontFamily:'monospace', fontWeight: active ? 700 : 400 } as React.CSSProperties),
  table:   { width:'100%', borderCollapse:'collapse' as const, marginBottom:'16px', fontSize:'12px' } as React.CSSProperties,
  th:      { textAlign:'left' as const, padding:'8px 12px', color:'#555', borderBottom:'1px solid #1a1a1a', fontSize:'10px', letterSpacing:'1px' } as React.CSSProperties,
  td:      { padding:'8px 12px', color:'#aaa', borderBottom:'1px solid #0a0a0a', verticalAlign:'top' as const } as React.CSSProperties,
};

const isPrivacy = window.location.pathname.startsWith('/privacy');

function Heading({ children }: { children: React.ReactNode }) {
  return <h2 style={S.h2}>{children}</h2>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p style={S.p}>{children}</p>;
}
function Li({ children }: { children: React.ReactNode }) {
  return <li style={S.li}>{children}</li>;
}

function TermsContent() {
  return (
    <>
      <h1 style={S.h1}>TERMS OF SERVICE</h1>
      <div style={S.meta}>Black Top TMS — DNA Global Enterprises | Effective: May 1, 2026</div>

      <Heading>1. Agreement to Terms</Heading>
      <P>These Terms of Service constitute a legally binding agreement between you ("Carrier") and DNA Global Enterprises ("we," "us") governing your access to and use of the Black Top TMS platform at app.blacktopsystem.com. By creating an account or using the Service you agree to these Terms. If you do not agree, do not use the Service.</P>

      <Heading>2. Description of Service</Heading>
      <P>Black Top TMS is a cloud-based transportation management system providing load management, AI-powered dispatch intelligence (Jake), driver mobile application, EDI integration, ELD/telematics integration, email-to-load automation, factoring integration, document OCR, IFTA reporting, load board integration, customer tracking, QuickBooks export, and driver communications.</P>

      <Heading>3. Account Registration and Eligibility</Heading>
      <P>You must be at least 18 years of age and a licensed motor carrier, freight broker, or authorized dispatch service operating under applicable FMCSA regulations. You are responsible for maintaining the confidentiality of your credentials and all activity under your account. Notify us at <span style={S.strong}>support@blacktopsystem.com</span> immediately of any unauthorized access.</P>

      <Heading>4. Subscription Plans and Payment</Heading>
      <P>The Service is offered under four tiers: Solo ($199/mo), Small Fleet ($349/mo), Growth ($599/mo), and Fleet ($999/mo). A one-time non-refundable startup fee applies to each new subscription. New accounts receive a 14-day free trial. Monthly fees are billed in advance via Stripe and are non-refundable. Price changes require 30 days written notice. Accounts more than 15 days past due may be suspended. Data is retained 60 days following suspension before permanent deletion.</P>

      <Heading>5. Acceptable Use</Heading>
      <P>You agree to use the Service only for lawful freight transportation business purposes. You agree NOT to: dispatch loads in violation of FMCSA HOS regulations, transmit false load information, reverse engineer the Service, share credentials with unauthorized third parties, upload malicious code, attempt to access other carriers' data, or use Jake AI to generate fraudulent communications.</P>

      <Heading>6. Jake AI — Limitations and Disclaimers</Heading>
      <P>Jake's recommendations, alerts, and responses are informational only and do not constitute legal, regulatory, safety, or operational advice. Dispatching decisions remain the sole responsibility of the Carrier. ETA calculations are estimates. We are not liable for the content of AI-generated communications sent on your behalf.</P>

      <Heading>7. Third-Party Integrations</Heading>
      <P>The Service integrates with ELD vendors, load boards, factoring companies, SendGrid, VAPI, Anthropic, Stripe, Browserless, and Supabase. Your use of third-party integrations is subject to those providers' own terms. We are not responsible for the availability or accuracy of third-party services. Load board credentials are used only to search for loads on your behalf.</P>

      <Heading>8. Data Ownership and License</Heading>
      <P>You retain full ownership of your data. You grant us a limited, non-exclusive license to process your data solely to provide the Service. We do not sell your data or use it to train AI models. Upon termination, data export is available for 60 days, after which data may be permanently deleted.</P>

      <Heading>9. Limitation of Liability</Heading>
      <P>THE SERVICE IS PROVIDED "AS IS." WE ARE NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES INCLUDING LOST PROFITS, CARGO CLAIMS, OR REGULATORY FINES. OUR TOTAL LIABILITY SHALL NOT EXCEED SUBSCRIPTION FEES PAID IN THE THREE MONTHS PRECEDING THE CLAIM.</P>

      <Heading>10. Indemnification</Heading>
      <P>You agree to indemnify DNA Global Enterprises from claims arising from your use of the Service, your violation of these Terms, or disputes between you and a driver, broker, or shipper facilitated through the Service.</P>

      <Heading>11. Termination</Heading>
      <P>You may cancel at any time by contacting support@blacktopsystem.com. Access continues through the end of the current billing period. We may suspend accounts for Terms violations, non-payment, or fraudulent activity.</P>

      <Heading>12. Governing Law</Heading>
      <P>These Terms are governed by the laws of the State of Texas. Disputes shall be resolved by binding arbitration under AAA rules. You waive the right to participate in class action lawsuits.</P>

      <Heading>13. Contact</Heading>
      <P>DNA Global Enterprises | support@blacktopsystem.com | app.blacktopsystem.com</P>
    </>
  );
}

function PrivacyContent() {
  return (
    <>
      <h1 style={S.h1}>PRIVACY POLICY</h1>
      <div style={S.meta}>Black Top TMS — DNA Global Enterprises | Effective: May 1, 2026</div>

      <Heading>1. Overview</Heading>
      <P>DNA Global Enterprises operates Black Top TMS at app.blacktopsystem.com. This Privacy Policy explains how we collect, use, store, and protect your information. Your operational data belongs to you. We do not sell it, share it with competitors, or use it for advertising.</P>

      <Heading>2. Information We Collect</Heading>
      <P><span style={S.strong}>You provide:</span> Company name, email, billing info, driver records (CDL, DOT physical dates, contact info, pay rates), load data (routes, rates, commodity, customer names), BOL/POD documents, financial records, CRM contacts, and ELD/load board credentials (encrypted at rest).</P>
      <P><span style={S.strong}>Collected automatically:</span> Usage data, device information, IP address, and GPS location from your ELD provider (city/state displayed; coordinates stored but not shared with third parties).</P>
      <P><span style={S.strong}>From third parties:</span> GPS, HOS status, and vehicle data from your ELD provider; payment status from Stripe; load listing data from load boards using your credentials.</P>

      <Heading>3. How We Use Your Information</Heading>
      <P>We use your information to: provide the Service, power Jake AI responses and alerts, send automated emails (rate confirmations, delay notices, weekly briefs) on your behalf, process EDI transactions, generate IFTA and financial reports, process BOL/POD documents via Claude Vision, process payments, detect fraud, provide support, and send service notifications. We do not use your operational data to train AI models.</P>

      <Heading>4. Sub-Processors</Heading>
      <table style={S.table}>
        <thead><tr><th style={S.th}>Provider</th><th style={S.th}>Purpose</th><th style={S.th}>Data Shared</th></tr></thead>
        <tbody>
          {[
            ['Supabase','Database and auth','All platform data'],
            ['Anthropic (Claude)','OCR, Jake AI, financial briefs','Documents, load data, driver data'],
            ['VAPI','Jake voice AI','Voice transcripts, dispatch context'],
            ['SendGrid','Email delivery','Email addresses, email content'],
            ['Stripe','Payment processing','Billing information'],
            ['Vercel','Web hosting','Web traffic'],
            ['Browserless','Load board automation','Search queries'],
          ].map(([p,u,d]) => (
            <tr key={p}><td style={S.td}>{p}</td><td style={S.td}>{u}</td><td style={S.td}>{d}</td></tr>
          ))}
        </tbody>
      </table>

      <Heading>5. Data Security</Heading>
      <P>All data in transit is encrypted with TLS 1.2+. Sensitive credentials are encrypted with AES-256 at rest. Row-level security in our database ensures your company's data is completely isolated from other carriers. In the event of a breach affecting your data, we will notify you within 72 hours.</P>

      <Heading>6. Driver Data</Heading>
      <P>Driver GPS location is used only for dispatch within your account and Jake's fleet monitoring. Exact coordinates are stored but never shared with third parties outside your configured integrations. CDL numbers, DOT physical dates, and drug test records are visible only to authorized users within your company account.</P>

      <Heading>7. Data Retention</Heading>
      <ul>
        <Li>Active account data: retained for the duration of your subscription</Li>
        <Li>Load history: 3 years from delivery date</Li>
        <Li>Financial records: 7 years from transaction date</Li>
        <Li>BOL/POD documents: 2 years from upload date</Li>
        <Li>Deleted accounts: permanently deleted within 60 days of account closure</Li>
      </ul>

      <Heading>8. Your Rights</Heading>
      <P>You have the right to access, correct, delete, and export your data at any time. To exercise these rights, contact support@blacktopsystem.com. We will respond within 30 days.</P>

      <Heading>9. Changes to This Policy</Heading>
      <P>We will notify you of material changes by email at least 30 days before they take effect. Continued use of the Service constitutes acceptance.</P>

      <Heading>10. Contact</Heading>
      <P>DNA Global Enterprises | support@blacktopsystem.com | app.blacktopsystem.com</P>
    </>
  );
}

export default function LegalPage() {
  return (
    <div style={S.root}>
      <div style={S.header}>
        <a href="/" style={{ ...S.logo, textDecoration:'none' }}>BLACK TOP TMS</a>
        <nav style={S.nav}>
          <a href="/terms"   style={S.navLink(!isPrivacy)}>Terms of Service</a>
          <a href="/privacy" style={S.navLink(isPrivacy)}>Privacy Policy</a>
          <a href="/"        style={{ ...S.navLink(false), marginLeft:'16px' }}>← Back to app</a>
        </nav>
      </div>
      <div style={S.body}>
        {isPrivacy ? <PrivacyContent /> : <TermsContent />}
      </div>
    </div>
  );
}
